/**
 * Product Page - Variant Selection System
 * 
 * A robust variant selection system that:
 * - Supports any number of product options (1, 2, or 3)
 * - Dynamically updates availability based on selections
 * - Updates prices, images, and button states
 * - Works with color swatches, size buttons, and any option type
 * 
 * @requires window.product - Product JSON object from Shopify
 */

(function() {
  'use strict';

  class ProductVariantSelector {
    constructor() {
      this.product = window.product;
      this.form = document.querySelector('[data-product-form]');
      this.variantInput = document.querySelector('[data-variant-input]');
      this.addToCartBtn = document.querySelector('[data-add-to-cart]');
      
      // Store current selections by option index (1-based)
      this.selectedOptions = {};
      
      // Cache DOM elements
      this.optionSelectors = document.querySelectorAll('[data-option-selector]');
      this.priceEl = document.querySelector('[data-product-price]');
      this.comparePriceEl = document.querySelector('[data-product-compare-price]');
      this.priceWrapper = document.querySelector('.main-product__price');
      
      if (!this.product || !this.form) {
        console.warn('ProductVariantSelector: Missing product data or form');
        return;
      }

      this.init();
    }

    init() {
      // Initialize with current variant — prefer the one Liquid selected via ?variant= param
      const form = this.form;
      const selectedVariantId = form ? parseInt(form.dataset.selectedVariantId, 10) : null;
      const initialVariant = (selectedVariantId && this.product.variants.find(v => v.id === selectedVariantId))
        || this.product.selected_or_first_available_variant
        || this.product.variants[0];
      
      if (initialVariant) {
        // Set initial selections from variant
        this.product.options.forEach((optionName, index) => {
          const optionIndex = index + 1;
          this.selectedOptions[optionIndex] = initialVariant[`option${optionIndex}`];
        });
      }

      this.bindEvents();
      this.updateAvailability();
      this.updateUI(initialVariant);

      // Navigate gallery to initial variant image once gallery is ready
      if (initialVariant?.featured_media) {
        this.changeMedia(initialVariant.featured_media.id);
      }

      this.initQuantitySelector();
      this.initAddToCart();
    }

    bindEvents() {
      // Bind all option buttons (swatches and size buttons)
      this.optionSelectors.forEach(selector => {
        const buttons = selector.querySelectorAll('[data-option-value]');
        buttons.forEach(button => {
          button.addEventListener('click', (e) => this.handleOptionClick(e, button));
        });
      });

      // Bind sticky bar size buttons
      const stickyBtns = document.querySelectorAll('[data-sticky-sizes] [data-option-value]');
      stickyBtns.forEach(button => {
        button.addEventListener('click', (e) => this.handleOptionClick(e, button));
      });
    }

    handleOptionClick(event, button) {
      event.preventDefault();
      
      const optionIndex = parseInt(button.dataset.optionIndex);
      const optionValue = button.dataset.optionValue;
      
      // Update selection
      this.selectedOptions[optionIndex] = optionValue;
      
      // Find matching variant
      const variant = this.findVariant();
      
      // Update UI
      this.updateActiveStates(optionIndex, optionValue);
      this.updateAvailability();
      this.updateUI(variant);
      
      // Handle media change for color swatches
      if (button.dataset.mediaId) {
        this.changeMedia(button.dataset.mediaId);
      } else if (variant && variant.featured_media) {
        this.changeMedia(variant.featured_media.id);
      }
      
      // Update URL without page reload
      if (variant) {
        this.updateURL(variant);
      }
    }

    /**
     * Find variant matching current selections
     * @returns {Object|null} Variant object or null
     */
    findVariant() {
      return this.product.variants.find(variant => {
        return this.product.options.every((optionName, index) => {
          const optionIndex = index + 1;
          return variant[`option${optionIndex}`] === this.selectedOptions[optionIndex];
        });
      });
    }

    /**
     * Update active states on option buttons
     */
    updateActiveStates(changedOptionIndex, selectedValue) {
      // Update the changed option's buttons in main picker
      const selector = document.querySelector(`[data-option-selector="${changedOptionIndex}"]`);
      if (selector) {
        selector.querySelectorAll('[data-option-value]').forEach(btn => {
          btn.classList.toggle('is-active', btn.dataset.optionValue === selectedValue);
        });
      }

      // Sync sticky bar size buttons
      const stickyContainer = document.querySelector('[data-sticky-sizes]');
      if (stickyContainer) {
        stickyContainer.querySelectorAll(`[data-option-index="${changedOptionIndex}"]`).forEach(btn => {
          btn.classList.toggle('is-active', btn.dataset.optionValue === selectedValue);
        });
      }
      
      // Update the selected value display
      const valueDisplay = document.querySelector(`[data-selected-option="${changedOptionIndex}"]`);
      if (valueDisplay) {
        valueDisplay.textContent = selectedValue;
      }
    }

    /**
     * Update availability states for all options based on current selections
     * This creates the "cross-out unavailable" effect
     */
    updateAvailability() {
      this.optionSelectors.forEach(selector => {
        const currentOptionIndex = parseInt(selector.dataset.optionSelector);
        const buttons = selector.querySelectorAll('[data-option-value]');
        
        buttons.forEach(button => {
          const testValue = button.dataset.optionValue;
          
          // Build test selections: current selections but with this option's value
          const testSelections = { ...this.selectedOptions };
          testSelections[currentOptionIndex] = testValue;
          
          // Check if any variant exists with these selections
          const hasAvailableVariant = this.product.variants.some(variant => {
            // Check if variant matches test selections
            const matchesSelections = this.product.options.every((optionName, index) => {
              const optionIndex = index + 1;
              return variant[`option${optionIndex}`] === testSelections[optionIndex];
            });
            
            return matchesSelections && variant.available;
          });
          
          // Also check if ANY variant with this value exists (for "sold out" vs "not a combination")
          const variantExists = this.product.variants.some(variant => {
            return variant[`option${currentOptionIndex}`] === testValue;
          });
          
          // Update button state
          button.classList.toggle('is-unavailable', !hasAvailableVariant);
          button.classList.toggle('is-sold-out', variantExists && !hasAvailableVariant);
          button.dataset.unavailable = (!hasAvailableVariant).toString();
        });
      });
    }

    /**
     * Update all UI elements based on variant
     */
    updateUI(variant) {
      this.updatePrice(variant);
      this.updateButton(variant);
      this.updateStockIndicator(variant);
    }

    updateStickyVariantLabel(variant) {
      const label = document.querySelector('[data-sticky-variant-label]');
      if (!label || !variant) return;
      label.textContent = variant.options.join(' / ');
    }

    /**
     * Update price display
     */
    updatePrice(variant) {
      if (!variant) return;
      
      const hasDiscount = variant.compare_at_price && variant.compare_at_price > variant.price;
      
      // Update current price
      if (this.priceEl) {
        this.priceEl.textContent = this.formatMoney(variant.price);
      }
      
      // Update compare price
      if (this.comparePriceEl) {
        if (hasDiscount) {
          this.comparePriceEl.textContent = this.formatMoney(variant.compare_at_price);
          this.comparePriceEl.style.display = '';
        } else {
          this.comparePriceEl.textContent = '';
          this.comparePriceEl.style.display = 'none';
        }
      }
      
      // Toggle sale class
      if (this.priceWrapper) {
        this.priceWrapper.classList.toggle('main-product__price--on-sale', hasDiscount);
      }
      
      // Update hidden input
      if (this.variantInput) {
        this.variantInput.value = variant.id;
      }
    }

    /**
     * Update add to cart button state
     */
    updateButton(variant) {
      if (!this.addToCartBtn) return;
      
      const btn = this.addToCartBtn;
      const addText = btn.dataset.addToCartText || window.theme?.strings?.addToCart || '';
      const soldOutText = btn.dataset.soldOutText || window.theme?.strings?.soldOut || '';
      const unavailableText = btn.dataset.unavailableText || window.theme?.strings?.unavailable || '';
      const stickyBtn = document.querySelector('[data-sticky-add-to-cart]');

      let labelText;
      let priceHTML = '';

      if (!variant) {
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.classList.add('is-disabled');
        labelText = unavailableText;
      } else if (variant.available) {
        this.addToCartBtn.disabled = false;
        this.addToCartBtn.classList.remove('is-disabled');
        labelText = addText;
        const comparePrice = parseInt(variant.compare_at_price, 10) || 0;
        const currentPrice = parseInt(variant.price, 10) || 0;
        const hasDiscount = comparePrice > 0 && comparePrice > currentPrice;
        if (hasDiscount) {
          priceHTML = ` <span class="main-product__add-btn-prices"><s class="main-product__add-btn-compare">${this.formatMoney(comparePrice)}</s> ${this.formatMoney(currentPrice)}</span>`;
        } else if (currentPrice) {
          priceHTML = ` <span class="main-product__add-btn-prices">${this.formatMoney(currentPrice)}</span>`;
        }
      } else {
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.classList.add('is-disabled');
        labelText = soldOutText;
      }

      this.addToCartBtn.innerHTML = `<span class="main-product__add-btn-label">${labelText}</span>` + priceHTML;
      if (stickyBtn) stickyBtn.innerHTML = `<span class="main-product__add-btn-label">${labelText}</span>` + priceHTML;
    }

    /**
     * Update stock indicator if present
     */
    updateStockIndicator(variant) {
      const stockIndicator = document.querySelector('[data-stock-indicator]');
      if (!stockIndicator || !variant) return;
      
      const inStock = stockIndicator.querySelector('.main-product__stock--in-stock');
      const lowStock = stockIndicator.querySelector('.main-product__stock--low-stock');
      const outOfStock = stockIndicator.querySelector('.main-product__stock--out-of-stock');
      
      if (inStock) inStock.style.display = variant.available ? '' : 'none';
      if (lowStock) lowStock.style.display = 'none';
      if (outOfStock) outOfStock.style.display = variant.available ? 'none' : '';
    }

    /**
     * Change gallery media (for color swatches)
     */
    changeMedia(mediaId) {
      if (!mediaId) return;
      
      // Dispatch event for gallery to handle
      document.dispatchEvent(new CustomEvent('product:mediaChange', {
        detail: { mediaId: mediaId },
        bubbles: true
      }));
      
      // Direct gallery integration
      if (window.productGallery?.goToMediaById) {
        window.productGallery.goToMediaById(mediaId);
      } else {
        // Gallery not ready yet — store for when it initializes
        window._pendingMediaId = mediaId;
      }
    }

    /**
     * Update URL with variant ID
     */
    updateURL(variant) {
      if (!variant || !window.history.replaceState) return;
      
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    }

    /**
     * Format money value
     */
    formatMoney(cents) {
      if (window.ThemeUtils?.formatMoney) {
        return window.ThemeUtils.formatMoney(cents);
      }
      
      const moneyFormat = window.Shopify?.money_format || '${{amount}}';
      const value = (cents / 100).toFixed(2);
      
      if (moneyFormat.includes('{{amount_with_comma_separator}}')) {
        return moneyFormat.replace('{{amount_with_comma_separator}}', value.replace('.', ','));
      }
      if (moneyFormat.includes('{{amount_no_decimals}}')) {
        return moneyFormat.replace('{{amount_no_decimals}}', Math.round(cents / 100).toString());
      }
      if (moneyFormat.includes('{{amount}}')) {
        return moneyFormat.replace('{{amount}}', value);
      }
      
      return `$${value}`;
    }

    /**
     * Initialize quantity selector
     */
    initQuantitySelector() {
      const quantityInput = document.querySelector('.main-product__quantity-input');
      const minusBtn = document.querySelector('[data-quantity-minus]');
      const plusBtn = document.querySelector('[data-quantity-plus]');

      if (!quantityInput || !minusBtn || !plusBtn) return;

      minusBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const currentValue = parseInt(quantityInput.value) || 1;
        if (currentValue > 1) {
          quantityInput.value = currentValue - 1;
        }
      });

      plusBtn.addEventListener('click', (e) => {
        e.preventDefault();
        const currentValue = parseInt(quantityInput.value) || 1;
        const maxValue = parseInt(quantityInput.max) || 99;
        if (currentValue < maxValue) {
          quantityInput.value = currentValue + 1;
        }
      });

      quantityInput.addEventListener('change', () => {
        let value = parseInt(quantityInput.value) || 1;
        const min = parseInt(quantityInput.min) || 1;
        const max = parseInt(quantityInput.max) || 99;
        
        if (value < min) value = min;
        if (value > max) value = max;
        
        quantityInput.value = value;
      });
    }

    /**
     * Initialize add to cart functionality
     */
    initAddToCart() {
      if (!this.addToCartBtn || !this.form) return;

      // Prevent default form submission
      this.form.addEventListener('submit', (e) => e.preventDefault());

      this.addToCartBtn.addEventListener('click', async (e) => {
        e.preventDefault();
        
        const variantId = this.variantInput?.value;
        const quantityInput = this.form.querySelector('[name="quantity"]');
        const quantity = quantityInput ? parseInt(quantityInput.value) || 1 : 1;
        
        if (!variantId) {
          console.warn('No variant selected');
          return;
        }

        // Find current variant
        const variant = this.product.variants.find(v => v.id.toString() === variantId);
        
        // Set loading state
        this.addToCartBtn.disabled = true;
        this.addToCartBtn.classList.add('is-loading');
        const originalText = this.addToCartBtn.textContent;
        this.addToCartBtn.textContent = window.theme?.strings?.adding || 'Adding...';

        try {
          const response = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              id: parseInt(variantId, 10),
              quantity: quantity
            })
          });

          if (!response.ok) {
            const error = await response.json();
            throw new Error(error.description || 'Error adding to cart');
          }

          const data = await response.json();
          
          // Dispatch cart updated event
          document.dispatchEvent(new CustomEvent('cart:updated', { 
            detail: { item: data },
            bubbles: true 
          }));
          
          // Open cart drawer
          if (window.cartManager?.loadCartData) {
            await window.cartManager.loadCartData(true);
          } else {
            // Fallback: click cart toggle
            const cartToggle = document.querySelector('[data-drawer-toggle="cart"]');
            if (cartToggle) cartToggle.click();
          }

          // Success state
          this.addToCartBtn.classList.remove('is-loading');
          this.addToCartBtn.textContent = window.theme?.strings?.added || 'Added!';
          
          setTimeout(() => {
            if (variant?.available) {
              this.addToCartBtn.disabled = false;
              this.addToCartBtn.textContent = originalText;
            }
          }, 1500);
          
        } catch (error) {
          console.error('Add to cart error:', error);
          
          this.addToCartBtn.classList.remove('is-loading');
          this.addToCartBtn.textContent = window.theme?.strings?.errorTryAgain || 'Error - Try again';
          
          setTimeout(() => {
            if (variant?.available) {
              this.addToCartBtn.disabled = false;
              this.addToCartBtn.textContent = originalText;
            }
          }, 2000);
        }
      });
    }
  }

  /**
   * Sticky Add to Cart Bar
   */
  function initStickyBar() {
    const cartActions = document.querySelector('.main-product__cart-actions');
    const stickyBar = document.querySelector('[data-sticky-bar]');
    if (!cartActions || !stickyBar) return;

    // Move sticky bar to body to avoid iOS position:fixed issues inside section wrappers
    document.body.appendChild(stickyBar);

    // Elements
    const originalInput = document.querySelector('[data-quantity-input]');
    const stickyInput = stickyBar.querySelector('[data-sticky-quantity-input]');
    const originalBtn = document.querySelector('[data-add-to-cart]');
    const stickyBtn = stickyBar.querySelector('[data-sticky-add-to-cart]');
    const stickyMinus = stickyBar.querySelector('[data-sticky-quantity-minus]');
    const stickyPlus = stickyBar.querySelector('[data-sticky-quantity-plus]');

    // Show/hide logic
    const footer = document.querySelector('.footer') || document.querySelector('footer');
    
    function isFooterVisible() {
      if (!footer) return false;
      const footerRect = footer.getBoundingClientRect();
      return footerRect.top < window.innerHeight;
    }

    function updateStickyVisibility() {
      const rect = cartActions.getBoundingClientRect();
      const isOutOfView = rect.bottom < 0 || rect.top > window.innerHeight;
      if (isOutOfView && !isFooterVisible()) {
        stickyBar.classList.add('is-visible');
      } else {
        stickyBar.classList.remove('is-visible');
      }
    }

    // Use IntersectionObserver + scroll fallback for reliability on iOS
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (!entry.isIntersecting && !isFooterVisible()) {
            stickyBar.classList.add('is-visible');
          } else {
            stickyBar.classList.remove('is-visible');
          }
        });
      }, { threshold: 0 });
      observer.observe(cartActions);

      // Also observe footer to react when it enters/leaves viewport
      if (footer) {
        const footerObserver = new IntersectionObserver(() => {
          updateStickyVisibility();
        }, { threshold: 0 });
        footerObserver.observe(footer);
      }
    }

    // Scroll fallback for extra reliability
    let scrollTicking = false;
    window.addEventListener('scroll', () => {
      if (!scrollTicking) {
        scrollTicking = true;
        requestAnimationFrame(() => {
          updateStickyVisibility();
          scrollTicking = false;
        });
      }
    }, { passive: true });

    // Initial check
    updateStickyVisibility();

    // Quantity buttons for sticky bar
    if (stickyMinus && stickyInput) {
      stickyMinus.addEventListener('click', (e) => {
        e.preventDefault();
        const val = parseInt(stickyInput.value) || 1;
        if (val > 1) {
          stickyInput.value = val - 1;
          syncQuantity(stickyInput, originalInput);
        }
      });
    }
    if (stickyPlus && stickyInput) {
      stickyPlus.addEventListener('click', (e) => {
        e.preventDefault();
        const val = parseInt(stickyInput.value) || 1;
        if (val < 99) {
          stickyInput.value = val + 1;
          syncQuantity(stickyInput, originalInput);
        }
      });
    }

    // Bidirectional quantity sync
    function syncQuantity(source, target) {
      if (target) target.value = source.value;
    }

    if (originalInput && stickyInput) {
      originalInput.addEventListener('change', () => syncQuantity(originalInput, stickyInput));
      stickyInput.addEventListener('change', () => syncQuantity(stickyInput, originalInput));
    }

    // Sticky add-to-cart delegates to original button
    if (stickyBtn && originalBtn) {
      stickyBtn.addEventListener('click', (e) => {
        e.preventDefault();
        originalBtn.click();
      });

      // Sync button state from original to sticky
      const syncBtnState = () => {
        stickyBtn.disabled = originalBtn.disabled;
        stickyBtn.innerHTML = originalBtn.innerHTML;
        stickyBtn.className = originalBtn.className;
      };

      // Observe changes on original button
      const btnObserver = new MutationObserver(syncBtnState);
      btnObserver.observe(originalBtn, {
        attributes: true,
        childList: true,
        characterData: true,
        subtree: true
      });

      // Initial sync so sticky matches the already-translated original button
      syncBtnState();
    }
  }

  // Initialize when DOM is ready
  function init() {
    try {
      new ProductVariantSelector();
    } catch (e) {
      console.error('ProductVariantSelector init error:', e);
    }
    initStickyBar();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Export for external use
  window.ProductVariantSelector = ProductVariantSelector;
})();
